// Figures shown under a coaching letter: when it was written, how much the coach thought,
// what it cost against the estimate, and this month's running totals. Times and months
// are US Central, where Plumb's user lives.

const ZONE = 'America/Chicago'

const timeFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONE, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
})
const monthFormat = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, year: 'numeric', month: '2-digit' })
const monthName = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, month: 'long' })

/** "Sep 27, 2026, 8:55 AM CT" */
export function formatCentralTime(iso: string): string {
  return `${timeFormat.format(new Date(iso))} CT`
}

/** "2026-09": the calendar month an instant falls in, in US Central. */
export function centralMonthKey(date: Date): string {
  const parts = Object.fromEntries(monthFormat.formatToParts(date).map((p) => [p.type, p.value]))
  return `${parts['year']}-${parts['month']}`
}

/** "September", in US Central. */
export function centralMonthName(date: Date): string {
  return monthName.format(date)
}

/** Words in the coach's summarized thinking; 0 when there was none. */
export function thinkingWords(thinking: string | null | undefined): number {
  const text = thinking?.trim()
  return text ? text.split(/\s+/).length : 0
}

/** How far the actual cost landed from the estimate, as a whole percent (+ is over). */
export function costDeltaPercent(actualUsd: number, estimatedUsd: number): number | null {
  if (!(estimatedUsd > 0)) return null
  return Math.round(((actualUsd - estimatedUsd) / estimatedUsd) * 100)
}

interface CostedLetter {
  createdAt: string
  costUsd?: number | null
}

/** Letters written this month (US Central) and what they cost; letters without a cost count as $0. */
export function monthTotals(letters: CostedLetter[], now: Date = new Date()): { letters: number; costUsd: number } {
  const month = centralMonthKey(now)
  const mine = letters.filter((l) => centralMonthKey(new Date(l.createdAt)) === month)
  return { letters: mine.length, costUsd: mine.reduce((sum, l) => sum + (l.costUsd ?? 0), 0) }
}

/** Prepaid credit left after every costed letter, never below zero. Approximate: Plumb only sees its own letters. */
export function creditLeft(creditUsd: number, letters: CostedLetter[]): number {
  const spent = letters.reduce((sum, l) => sum + (l.costUsd ?? 0), 0)
  return Math.max(0, creditUsd - spent)
}
